/**
 * useCapgoVersion — which JS bundle is actually running on this device.
 *
 * Support's first question on any mobile report is "what version are you
 * on?", and with OTA that has two answers: the native shell from the
 * store (versionName + versionCode) and the Capgo bundle running inside
 * it, which moves independently of it. This reports both so the account
 * sheet can show them under Sign out.
 *
 * Reads only — the update cycle itself lives in `useCapgoUpdate`.
 *
 * No-ops on web builds (the `isMobileBuild` guard skips both dynamic
 * imports), so the same Header on a narrow desktop window shows nothing.
 */

import * as React from "react";

import { APP_VERSION, CAPGO_CHANNEL, isMobileBuild } from "@/platform";

export interface CapgoVersionInfo {
  /** Version of the JS bundle currently executing. */
  bundleVersion: string;
  /** True when that bundle is the one shipped inside the APK/IPA. */
  isBuiltIn: boolean;
  /** OTA channel baked in at build time (development / production). */
  channel: string;
  /** Native shell — `versionName` and `versionCode` from the store build. */
  nativeVersion: string | null;
  nativeBuild: string | null;
}

/**
 * Capgo doesn't report a real version for the bundle shipped in the
 * binary: `current()` either throws `already_on_builtin` (see the call
 * order note in `useCapgoUpdate`) or returns the `builtin` sentinel. Both
 * mean the same thing — no OTA bundle is active — so fall back to the
 * package.json version the shell was built from rather than showing a
 * sentinel string to the user.
 */
export function resolveBundleVersion(
  raw: string | null | undefined,
  appVersion: string = APP_VERSION,
): { bundleVersion: string; isBuiltIn: boolean } {
  const version = (raw ?? "").trim();
  if (!version || version === "builtin" || version === "unknown") {
    return { bundleVersion: appVersion, isBuiltIn: true };
  }
  return { bundleVersion: version, isBuiltIn: false };
}

export function useCapgoVersion(): CapgoVersionInfo | null {
  const [info, setInfo] = React.useState<CapgoVersionInfo | null>(null);

  React.useEffect(() => {
    if (!isMobileBuild) return;

    // Guard against the async imports resolving after unmount — the sheet
    // is closable long before the native bridge answers on a cold start.
    let cancelled = false;

    void (async () => {
      let rawVersion: string | null = null;
      try {
        const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
        rawVersion = (await CapacitorUpdater.current()).bundle.version;
      } catch {
        // `already_on_builtin` on the native bundle — resolved below.
      }

      let nativeVersion: string | null = null;
      let nativeBuild: string | null = null;
      try {
        const { App } = await import("@capacitor/app");
        const appInfo = await App.getInfo();
        nativeVersion = appInfo.version;
        nativeBuild = appInfo.build;
      } catch {
        // getInfo() is unavailable in some build flavours — the Capgo
        // line still stands on its own without the shell version.
      }

      if (cancelled) return;
      const { bundleVersion, isBuiltIn } = resolveBundleVersion(rawVersion);
      setInfo({
        bundleVersion,
        isBuiltIn,
        channel: CAPGO_CHANNEL,
        nativeVersion,
        nativeBuild,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return info;
}
